import classNames from "classnames";
import {
  FormEvent,
  KeyboardEvent,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import useTranslation from "../../hooks/useTranslation";
import { ChatMessage, MAX_QUESTION_LENGTH } from "../../types/aiCoach";
import { generateMessageId } from "../../utils/chatbot.utils";
import { Button } from "../ui/Button/Button";
import { Input } from "../ui/Input/Input";
import { LoadingIndicator } from "../ui/LoadingIndicator/LoadingIndicator";
import { Typography } from "../ui/Typography/Typography";
import { Direction, IconTypes } from "../ui/icons";
import { Icon } from "../ui/icons/Icon";
import "./TrainingChatBot.scss";

export interface TrainingChatBotProps {
  trainingModuleTitle?: string;
  /** Seed value for uncontrolled mode (Storybook / standalone). */
  initialMessages?: ChatMessage[];
  /**
   * Controlled conversation. When provided, the component renders this list
   * (greeting still prepended) and skips internal state + placeholder replies.
   */
  messages?: ChatMessage[];
  isReplying?: boolean;
  /** When true, locks the composer (e.g. learner has hit the chat quota). */
  disabled?: boolean;
  onSend?: (message: string) => void;
  onClose?: () => void;
  headerSlot?: ReactNode;
  className?: string;
}

export const TrainingChatBot = ({
  trainingModuleTitle,
  initialMessages,
  messages: messagesProp,
  isReplying: isReplyingProp,
  disabled = false,
  onSend,
  onClose,
  headerSlot,
  className,
}: TrainingChatBotProps) => {
  const isControlled = messagesProp !== undefined;
  const { t } = useTranslation("trainingCompletionPage");

  const resolvedTitle = trainingModuleTitle || t("titleFallback");

  const greeting = useMemo<ChatMessage>(
    () => ({
      id: "greeting",
      role: "assistant",
      message: resolvedTitle
        ? t("aiCoach.greetingWithTitle", { trainingTitle: resolvedTitle })
        : t("aiCoach.greeting"),
    }),
    [t, resolvedTitle]
  );

  const [internalConversation, setInternalConversation] = useState<
    ChatMessage[]
  >(initialMessages ?? []);
  const [input, setInput] = useState("");
  const [internalIsReplying, setInternalIsReplying] = useState(false);

  const conversation = isControlled ? messagesProp : internalConversation;
  const isReplying = isReplyingProp ?? internalIsReplying;

  // Always render the latest greeting (derived from props) at the top, then the
  // accumulated conversation. Storing the greeting in state would freeze it on
  // first render and miss later updates to trainingTitle.
  const messages = useMemo<ChatMessage[]>(
    () => [greeting, ...conversation],
    [greeting, conversation]
  );

  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const wasReplyingRef = useRef(isReplying);

  // Coalesce optimistic-append + isReplying-flip into a single rAF-scoped scroll
  // so back-to-back updates don't fight each other with overlapping smooth scrolls.
  useEffect(() => {
    const id = requestAnimationFrame(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    });
    return () => cancelAnimationFrame(id);
  }, [messages, isReplying]);

  useEffect(() => {
    if (wasReplyingRef.current && !isReplying) {
      inputRef.current?.focus();
    }
    wasReplyingRef.current = isReplying;
  }, [isReplying]);

  const handleSend = () => {
    const trimmed = input.trim();
    if (!trimmed || isReplying || disabled) return;

    setInput("");
    onSend?.(trimmed);

    // Controlled mode: parent owns messages + replying state; we just notify.
    if (isControlled) return;

    // Uncontrolled mode (Storybook / standalone): append optimistic user
    // message and simulate a placeholder assistant reply.
    const userMessage: ChatMessage = {
      id: generateMessageId(),
      role: "user",
      message: trimmed,
    };
    setInternalConversation((prev) => [...prev, userMessage]);

    if (isReplyingProp === undefined) {
      setInternalIsReplying(true);
      setTimeout(() => {
        setInternalConversation((prev) => [
          ...prev,
          {
            id: generateMessageId(),
            role: "assistant",
            message: t("aiCoach.placeholderReply"),
          },
        ]);
        setInternalIsReplying(false);
      }, 600);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    handleSend();
  };

  const counterId = "TrainingChatBot__composer__counter";

  return (
    <div className={classNames("TrainingChatBot", className)}>
      <div className="TrainingChatBot__header">
        {headerSlot && (
          <div className="TrainingChatBot__header__slot">{headerSlot}</div>
        )}
        <span aria-hidden="true" className="TrainingChatBot__header__icon">
          <Icon type={IconTypes.ChatPerson} color="secondary" size={24} />
        </span>
        <Typography
          desktop="button1"
          mobile="button2"
          color="darkerStoneBlue"
          weight="bold"
          tagVariant="h2"
        >
          {t("aiCoach.header")}
        </Typography>
        {onClose && (
          <Button
            variant="text"
            icon
            aria-label={t("aiCoach.closeChat")}
            onClick={onClose}
            className="TrainingChatBot__header__close"
          >
            <span aria-hidden="true">
              <Icon type={IconTypes.Close} size={20} />
            </span>
          </Button>
        )}
      </div>

      <div
        className="TrainingChatBot__messages"
        role="log"
        aria-live="polite"
        aria-busy={isReplying}
      >
        {messages.map((msg) => (
          <div
            key={msg.id}
            className={classNames(
              "TrainingChatBot__message",
              `TrainingChatBot__message--${msg.role}`
            )}
          >
            <div className="TrainingChatBot__message__bubble">
              {msg.role === "user" ? (
                <Typography desktop="body2" mobile="body2" color="light">
                  {msg.message}
                </Typography>
              ) : (
                <div className="TrainingChatBot__message__bubble__markdown">
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    disallowedElements={["a"]}
                    unwrapDisallowed
                  >
                    {msg.message}
                  </ReactMarkdown>
                </div>
              )}
            </div>
          </div>
        ))}

        {isReplying && (
          <div className="TrainingChatBot__message TrainingChatBot__message--assistant">
            <div className="TrainingChatBot__message__bubble TrainingChatBot__message__bubble--typing">
              <LoadingIndicator />
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      <form
        className="TrainingChatBot__composer"
        onSubmit={handleSubmit}
        noValidate
      >
        <div className="TrainingChatBot__composer__field">
          <Input
            fullWidth
            multiline
            maxRows={4}
            size="small"
            placeholder={
              disabled
                ? t("aiCoach.placeholderDisabled")
                : t("aiCoach.placeholder")
            }
            value={input}
            disabled={isReplying || disabled}
            onChange={(event) =>
              setInput(event.target.value.slice(0, MAX_QUESTION_LENGTH))
            }
            onKeyDown={handleKeyDown}
            inputRef={inputRef}
            inputProps={{
              "aria-label": t("aiCoach.placeholder"),
              "aria-describedby": counterId,
              maxLength: MAX_QUESTION_LENGTH,
            }}
            className="TrainingChatBot__composer__input"
          />
          <span id={counterId} className="TrainingChatBot__composer__counter">
            <Typography
              desktop="caption"
              mobile="caption"
              tagVariant="span"
              color={
                input.length >= MAX_QUESTION_LENGTH ? "error" : "secondary"
              }
            >
              {input.length}/{MAX_QUESTION_LENGTH}
            </Typography>
          </span>
        </div>
        <Button
          type="submit"
          variant="contained"
          icon
          color="success"
          aria-label={t("aiCoach.send")}
          disabled={!input.trim() || isReplying || disabled}
          className="TrainingChatBot__composer__send"
        >
          <span aria-hidden="true">
            <Icon type={IconTypes.Arrow} direction={Direction.RIGHT} />
          </span>
        </Button>
      </form>
    </div>
  );
};
